import * as React from 'react'
import { Checkin, RatingScaleLabel } from 'store/modules/checkins'
import { CHECKIN_SUMMARY_PAGE_TRACK_EVENT_NAME } from '../utils'
import { CustomRadarDot } from 'components/skills-radar-chart/custom-radar-dot'
import { Position } from 'store/modules/positions'
import {
  SkillsRadarChartDataPoint,
  SkillsRadarChartSeries,
} from '../../../components/skills-radar-chart/skills-radar-chart.types'
import { resolveRatingLabel } from '../../../utils/resolve-rating-label'
import { sortAlphaNumberValues } from 'app/packs/src/utils/sort-helpers'
import { store } from 'store/index'
import { Team } from 'store/modules/teams'
import { trackEvent } from 'app/packs/src/services/event-tracker'

export class ChartsVm {
  constructor(private checkin: Checkin, private positionId?: string | null) {}

  private _lowestLabel: RatingScaleLabel | null | undefined = undefined

  get comparison() {
    if (!this.positionId || !this.position) return

    const requiredPositionSkills = store.positionSkills.forPosition(
      this.positionId
    )

    const finalLevelTotal = this.checkin.checkinSkills.reduce<number>(
      (total, checkinSkill) => {
        const requiredSkill = requiredPositionSkills.find(
          (ps) => ps.skill.id === checkinSkill.skill.id
        )

        if (!requiredSkill) return total
        if (checkinSkill.finalLevel) total += checkinSkill.finalLevel
        return total
      },
      0
    )

    if (finalLevelTotal === 0) return

    const requiredLevelTotal = requiredPositionSkills.reduce<number>(
      (total, positionSkill) => {
        total += positionSkill.level
        return total
      },
      0
    )

    const percentageDifference = Math.abs(
      100 - (requiredLevelTotal / finalLevelTotal) * 100
    ).toFixed(0)

    const delta =
      finalLevelTotal > requiredLevelTotal
        ? 1
        : finalLevelTotal < requiredLevelTotal
        ? -1
        : 0
    const label = this.checkin.ratingScaleLabels?.find(
      (l) => l.scaleValue === delta
    )
    const status = label?.labelSlug ?? null

    let text = null
    if (status !== null) {
      if (delta > 0) text = `${percentageDifference}% above`
      else if (delta === 0) text = label?.labelName ?? null
      else text = `${percentageDifference}% away`
    }

    const statusColor = resolveRatingLabel({
      status,
      ratingScaleLabels: this.checkin.ratingScaleLabels,
    }).color

    return {
      max: Math.max(requiredLevelTotal, finalLevelTotal),
      status,
      statusColor,
      text,
      value: finalLevelTotal,
    }
  }

  // Returns counts keyed by labelSlug (e.g. "meeting", "exceeding").
  // When a custom scale is active, skipped skills (no final level) are bucketed
  // under the lowest label slug (e.g. "working_towards") so they appear in that
  // segment of the donut chart rather than as a separate "Not Checked-In" slice.
  // 'not_checked_in' is only populated for orgs without a custom scale.
  // Initialises every label slug to 0 so callers always get a full picture.
  get levelCounts(): Record<string, number> & { not_checked_in: number } {
    const initial: Record<string, number> = { not_checked_in: 0 }
    ;(this.checkin.ratingScaleLabels ?? []).forEach((l) => {
      initial[l.labelSlug] = 0
    })

    return this.skillIds.reduce<Record<string, number>>((counts, skillId) => {
      const currentLevel = this.currentLevelForSkill(skillId)
      const requiredLevel = this.requiredLevelForSkill(skillId)

      if (currentLevel === null || requiredLevel === null) {
        const key = this.lowestLabel?.labelSlug ?? 'not_checked_in'
        counts[key] = (counts[key] ?? 0) + 1
      } else {
        const delta = currentLevel - requiredLevel
        const slug = this.checkin.labelSlugForDelta(delta)
        if (slug) counts[slug] = (counts[slug] ?? 0) + 1
      }

      return counts
    }, initial) as Record<string, number> & { not_checked_in: number }
  }

  get options() {
    const options: Record<
      string,
      { code: string; label: string; value: string }[]
    > = {}

    this.sortedTeams.forEach((team) => {
      this.positions.forEach((position) => {
        if (position.team?.id !== team.id) return

        position.disciplines.forEach((discipline) => {
          if (!discipline) return
          options[team.name] = options[team.name] || []

          options[team.name].push({
            code: position.positionCode(discipline.initials),
            label: this.positionOptionLabel(position),
            value: position.id,
          })
        })
      })
    })

    return Object.entries(options).map(([teamName, options]) => ({
      label: teamName,
      options: options
        .sort((a, b) => sortAlphaNumberValues(a.code, b.code))
        .map((option) => {
          return { label: option.label, value: option.value }
        }),
    }))
  }

  get positionName() {
    return this.position?.name || this.checkin.positionName || 'No position'
  }

  get series() {
    return {
      required: {
        colour: '#CECACA',
        skills: this.requiredSkills,
      },
      current: {
        colour: '#0D592D',
        dot: (chartData) => {
          const payload = chartData.payload

          const delta =
            payload.current == null ? null : payload.current - payload.required
          const colour =
            delta === null
              ? (payload.statusColor as string | undefined) ?? '#CECACA'
              : this.labelColorByScaleValue(delta) ?? '#BDA00F'

          return (
            <CustomRadarDot
              cx={chartData.cx}
              cy={chartData.cy}
              colour={colour}
              key={chartData.key}
            />
          )
        },
        skills: this.currentSkills,
      },
    } satisfies SkillsRadarChartSeries
  }

  applyPositionChange(positionId: string) {
    this.fetchPositionSkills(positionId)
    this.trackEvent('compare-position')
  }

  trackEvent(action: string) {
    trackEvent(CHECKIN_SUMMARY_PAGE_TRACK_EVENT_NAME, {
      action,
    })
  }

  private get authorsPositionId() {
    return this.checkin.author.position?.id
  }

  private get currentSkills() {
    return this.checkin.checkinSkills.reduce<SkillsRadarChartDataPoint[]>(
      (skills, checkinSkill) => {
        const requiredSkill = this.requiredSkills.find(
          (skill) => skill.id === checkinSkill.skill.id
        )

        if (requiredSkill && checkinSkill.finalLevel !== null) {
          skills.push({
            id: checkinSkill.skill.id,
            category: checkinSkill.categoryName || 'Uncategorised',
            level: checkinSkill.finalLevel,
            name: checkinSkill.skill.name,
            statusColor: checkinSkill.statusColor,
            statusLabel: checkinSkill.statusLabel,
          })
        }

        return skills
      },
      []
    )
  }

  private get position() {
    if (!this.positionId) return null

    return store.positions.byId(this.positionId)
  }

  private get positions() {
    return store.positions.forOrg
  }

  private get requiredSkills() {
    if (this.positionId) {
      return store.positionSkills
        .forPosition(this.positionId)
        .map((positionSkill) => {
          const checkinSkill = this.checkinSkillForSkill(positionSkill.skill.id)
          const isSkipped = !checkinSkill || checkinSkill.finalLevel === null
          return {
            id: positionSkill.skill.id,
            category: positionSkill.categoryName || 'Uncategorised',
            level: positionSkill.level,
            meta: isSkipped
              ? this.lowestLabel?.labelSlug ?? 'not_checked_in'
              : undefined,
            name: positionSkill.skill.name,
            statusColor: isSkipped ? this.lowestLabel?.color : undefined,
            statusLabel: isSkipped ? this.lowestLabel?.labelName : undefined,
          }
        })
    } else {
      return this.checkin.checkinSkills.map((checkinSkill) => {
        const isSkipped = checkinSkill.finalLevel === null
        return {
          id: checkinSkill.skill.id,
          category: checkinSkill.categoryName || 'Uncategorised',
          level: checkinSkill.requiredLevel,
          meta: isSkipped
            ? this.lowestLabel?.labelSlug ?? 'not_checked_in'
            : undefined,
          name: checkinSkill.skill.name,
          statusColor: isSkipped ? this.lowestLabel?.color : undefined,
          statusLabel: isSkipped ? this.lowestLabel?.labelName : undefined,
        }
      })
    }
  }

  private get skillIds() {
    const currentSkillIds = this.currentSkills.map((skill) => skill.id)
    const requiredSkillIds = this.requiredSkills.map((skill) => skill.id)

    return [...new Set(currentSkillIds.concat(requiredSkillIds))]
  }

  private get sortedTeams() {
    const authorsTeam = this.checkin.author.team
    const defaultTeams = authorsTeam ? [authorsTeam] : []

    return defaultTeams.concat(
      this.positions
        .reduce<Team[]>((teams, position) => {
          if (
            position.team &&
            !teams.find((team) => team.id === position.team.id) &&
            authorsTeam?.id !== position.team.id
          ) {
            teams.push(position.team)
          }

          return teams
        }, [])
        .sort((a, b) => (a.name > b.name ? 1 : -1))
    )
  }

  private checkinSkillForSkill(skillId: string) {
    return this.checkin.checkinSkills.find(
      (checkinSkill) => checkinSkill.skill.id === skillId
    )
  }

  private currentLevelForSkill(skillId: string) {
    const checkinSkill = this.checkinSkillForSkill(skillId)

    return checkinSkill ? checkinSkill.finalLevel : null
  }

  private positionOptionLabel(position: Position) {
    if (position.id !== this.authorsPositionId) return position.name

    const str =
      this.checkin.author.id === store.currentUser?.id
        ? 'your'
        : `${this.checkin.author.fname}'s`

    return `${position.name} (${str} position)`
  }

  private requiredLevelForSkill(skillId: string) {
    const requiredSkill = this.requiredSkills.find(
      (requiredSkill) => requiredSkill.id === skillId
    )

    return requiredSkill ? requiredSkill.level : null
  }

  private get lowestLabel(): RatingScaleLabel | null {
    if (this._lowestLabel !== undefined) return this._lowestLabel
    const labels = this.checkin.ratingScaleLabels ?? []
    this._lowestLabel = labels.length
      ? [...labels].sort((a, b) => a.scaleValue - b.scaleValue)[0]
      : null
    return this._lowestLabel
  }

  private labelColorByScaleValue(scaleValue: number): string | null {
    const label = this.checkin.ratingScaleLabels?.find(
      (l) => l.scaleValue === scaleValue
    )
    return resolveRatingLabel({
      status: label?.labelSlug ?? null,
      statusColor: label?.color ?? null,
    }).color
  }

  private async fetchPositionSkills(positionId: string) {
    return await store.positionSkills.fetchForPosition(positionId)
  }
}
